import { useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import toast from "react-hot-toast";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { useAuth } from "../../contexts/AuthContext";
import {
  rekeyCalendarMapping,
  suppressCalendarSync,
} from "../../lib/googleCalendarSync";
import { Task, List, TaskAttachment, TaskLink } from "../../types";
import { DEFAULT_LIST_EMOJI } from "../../constants/defaults";
import { placeConvertedTask } from "../../lib/placeTaskInCache";

/**
 * Returns a `convertSubtaskToTask(subtaskId)` that promotes a subtask into a
 * standalone task. It copies the subtask's fields onto a new task (inheriting
 * the parent's list), re-creates its tags, attachments, and links on the task
 * tables, re-keys the Google Calendar mapping so the existing event is
 * preserved, then deletes the original subtask. Stored attachment files are
 * left in place — only the DB rows are re-pointed, the same way `useMoveSubtask`
 * leaves files under the original parent's folder.
 *
 * **Server-first with explicit rollback**, mirroring the pre-migration implementation.
 * A single database transaction is not viable here: the calendar re-key is a
 * network call to the `google-calendar-sync` edge function that must run between
 * the child-copy and the original-row delete (the mapping row FK-cascades off
 * the subtask), so it cannot live inside a Postgres transaction. The multi-step
 * write is therefore sequential, and any step after the task-row insert rolls
 * back by deleting the new task (cascading its partially-copied children), which
 * leaves the user exactly where they started with the original subtask intact.
 *
 * Behavioural additions over the god-hook copy, matching every other migrated
 * mutation hook: failures are captured to Sentry (the god-hook used
 * `console.error`), the freshest `tasks`/`lists` are read from the query cache
 * at mutation time rather than a render-time snapshot, and the cache is
 * invalidated after a successful conversion. The observable end state is
 * identical.
 *
 * This is the query-cache-aware replacement for the pre-migration promote flow.
 */
export function useConvertSubtaskToTask() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return async (subtaskId: string): Promise<Task | null> => {
    if (!user) return null;

    // Read the freshest tasks/lists at mutation time (not a stale render-time
    // snapshot) so the promotion sees the same cache the UI renders from.
    const currentTasks =
      queryClient.getQueryData<Task[]>(queryKeys.tasks) ?? [];
    const currentLists =
      queryClient.getQueryData<List[]>(queryKeys.lists) ?? [];

    const parentTask = currentTasks.find((t) =>
      t.subtasks?.some((st) => st.id === subtaskId),
    );
    const subtask = parentTask?.subtasks?.find((st) => st.id === subtaskId);
    if (!parentTask || !subtask) return null;

    const sameListTasks = currentTasks.filter(
      (t) => t.list_id === parentTask.list_id,
    );
    const maxTaskOrder =
      sameListTasks.length > 0
        ? Math.max(...sameListTasks.map((t) => t.order_position))
        : 0;

    // Local rollback used whenever a step after the task-row insert fails:
    // delete the new task (which cascades any partially-copied children) so
    // the user is left exactly where they started, with the original sub-task
    // and all its data intact.
    const rollback = async (
      createdTaskId: string,
      reason: string,
      cause?: unknown,
    ) => {
      Sentry.captureException(cause ?? new Error(reason), {
        extra: { mutation: "useConvertSubtaskToTask", subtaskId, reason },
      });
      await supabase.from("tasks").delete().eq("id", createdTaskId);
      toast.error("Failed to convert to task. Please try again.");
    };

    // A task stores the same 1–4 range as a step. 4 is no priority.
    // Anything outside that range becomes no priority.
    const taskPriority =
      subtask.priority >= 1 && subtask.priority <= 4 ? subtask.priority : 4;

    // 1. Insert the new task row.
    const newTaskId = crypto.randomUUID();
    await suppressCalendarSync("task", newTaskId, user.id);
    const { data: taskData, error: taskError } = await supabase
      .from("tasks")
      .insert({
        id: newTaskId,
        user_id: user.id,
        list_id: parentTask.list_id,
        title: subtask.title,
        description: subtask.description ?? "",
        priority: taskPriority,
        completed: subtask.completed,
        scheduled_date: subtask.scheduled_date,
        scheduled_time: subtask.scheduled_time,
        deadline_date: subtask.deadline_date,
        duration: subtask.duration,
        persistent: subtask.persistent ?? false,
        order_position: maxTaskOrder + 100,
      })
      .select()
      .single();

    if (taskError) {
      Sentry.captureException(taskError, {
        extra: { mutation: "useConvertSubtaskToTask", subtaskId },
      });
      toast.error("Failed to convert to task. Please try again.");
      return null;
    }

    // 2. Copy children onto the new task tables. Each step aborts and rolls
    //    back if it fails, so the original sub-task and its children are
    //    never destroyed mid-flight.
    const stTags = subtask.tags || [];
    if (stTags.length > 0) {
      const { error } = await supabase
        .from("task_tags")
        .insert(
          stTags.map((tag) => ({ task_id: taskData.id, tag_id: tag.id })),
        );
      if (error) {
        await rollback(taskData.id, "Error migrating tags to task", error);
        return null;
      }
    }

    const newAttachments: TaskAttachment[] = [];
    const stAttachments = subtask.attachments || [];
    if (stAttachments.length > 0) {
      const { data, error } = await supabase
        .from("task_attachments")
        .insert(
          stAttachments.map((a) => ({
            task_id: taskData.id,
            file_name: a.file_name,
            file_path: a.file_path,
            file_size: a.file_size,
            mime_type: a.mime_type,
            storage_url: a.storage_url,
          })),
        )
        .select();
      if (error || !data) {
        await rollback(
          taskData.id,
          "Error migrating attachments to task",
          error ?? new Error("no data returned"),
        );
        return null;
      }
      newAttachments.push(...data);
    }

    const newLinks: TaskLink[] = [];
    const stLinks = subtask.links || [];
    if (stLinks.length > 0) {
      const { data, error } = await supabase
        .from("task_links")
        .insert(
          stLinks.map((l) => ({
            task_id: taskData.id,
            url: l.url,
            description: l.description,
          })),
        )
        .select();
      if (error || !data) {
        await rollback(
          taskData.id,
          "Error migrating links to task",
          error ?? new Error("no data returned"),
        );
        return null;
      }
      newLinks.push(...data);
    }

    // 3. Move the Google Calendar mapping from the sub-task to the new task.
    //    The actual calendar event is preserved (same event id, RSVPs, etc.);
    //    only the mapping row moves between tables. If the user wasn't synced,
    //    rekey is a no-op and returns ok=true.
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const list = currentLists.find((l) => l.id === parentTask.list_id);
    const fullTask: Task = {
      ...taskData,
      subtasks: [],
      tags: stTags,
      attachments: newAttachments,
      links: newLinks,
      list: list?.name || "",
      listEmoji: list?.emoji || DEFAULT_LIST_EMOJI,
    };

    const rekey = await rekeyCalendarMapping(
      { entity: "subtask", id: subtaskId },
      { entity: "task", id: fullTask.id, fields: fullTask },
      tz,
    );
    if (!rekey.ok) {
      await rollback(taskData.id, "rekey-mapping failed");
      return null;
    }

    // 4. Delete the original sub-task. The sub-task's calendar mapping was
    //    already moved in step 3, so the cascade-delete only removes the
    //    sub-task's children (already copied above).
    const { error: deleteError } = await supabase
      .from("subtasks")
      .delete()
      .eq("id", subtaskId);
    if (deleteError) {
      // The new task is fully functional; surface a softer error so the user
      // knows to refresh rather than treating the conversion as a total
      // failure (which it isn't — their data is on the new task).
      Sentry.captureException(deleteError, {
        extra: {
          mutation: "useConvertSubtaskToTask",
          subtaskId,
          reason: "delete-original-subtask",
        },
      });
      toast.error(
        "Converted, but the original sub-task could not be removed. Refresh to clean up.",
      );
    }

    queryClient.setQueryData<Task[]>(queryKeys.tasks, (prev) =>
      placeConvertedTask(prev, parentTask.id, subtaskId, fullTask),
    );

    queryClient.invalidateQueries({ queryKey: queryKeys.tasks });
    return fullTask;
  };
}
